/* import __COLOCATED_TEMPLATE__ from './phone-details-call-transcription.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import type {
  CallRecording,
  ArticleSource,
  Message,
  TriggeredGuidance,
} from 'embercom/objects/inbox/call-recording';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { deleteRequest } from 'embercom/lib/inbox/requests';
import type Snackbar from 'embercom/services/snackbar';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type Session from 'embercom/services/session';

interface PhoneDetailsCallTranscriptionSignature {
  Args: {
    recording: CallRecording;
    callId: number | string;
  };
  Element: HTMLDivElement;
}

export type TranscriptEntry =
  | {
      type: 'fin_searched';
      searchQuery: string;
      sources: ArticleSource[];
      triggeredGuidance: TriggeredGuidance[];
    }
  | {
      type: 'guidance_triggered';
      title?: string;
      ruleText?: string;
      guidanceUrl: string | null;
    }
  | { type: 'system'; content: string }
  | { type: 'message'; speakerLabel: string; startTime: string; content: string };

type Option = {
  text: string;
  icon: string;
  onSelectItem: () => void;
  isDestructive: boolean;
  isDisabled: boolean;
  tooltipText?: string;
};

export default class PhoneDetailsCallTranscription extends Component<PhoneDetailsCallTranscriptionSignature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare snackbar: Snackbar;
  @service declare inboxSidebarService: InboxSidebarService;

  @tracked showConfirmationModal = false;
  @tracked isDeleting = false;

  private readonly VOICEMAIL_PREFIX = 'voic';
  private readonly SPEAKER_PREFIX = 'spk_';
  private readonly PREFIX_LENGTH = 4;
  private readonly FIN_SEARCHED_PREFIX = 'fin_searched:';
  private readonly GUIDANCE_TRIGGERED_PREFIX = 'guidance_triggered:';

  get recordingUrl(): string | null {
    return this.args.recording.recordingUrl;
  }

  get transcriptUrl(): string {
    return this.args.recording.transcriptUrl ?? '';
  }

  get recordingDeleted(): boolean {
    return this.args.recording.isDeleted;
  }

  get hasTranscript(): boolean {
    return !!this.args.recording.jsonTranscript?.length;
  }

  get transcriptEntries(): TranscriptEntry[] {
    let messages = this.args.recording.jsonTranscript ?? [];

    return messages.map((message): TranscriptEntry => {
      if (!this.isSystemMessage(message)) {
        return {
          type: 'message',
          speakerLabel: this.speakerLabel(message),
          startTime: message.start_time,
          content: message.content,
        };
      }

      if (message.content.startsWith(this.FIN_SEARCHED_PREFIX)) {
        return {
          type: 'fin_searched',
          searchQuery: message.content.slice(this.FIN_SEARCHED_PREFIX.length),
          sources: message.article_sources ?? [],
          triggeredGuidance: message.triggered_guidance ?? [],
        };
      }

      if (message.content.startsWith(this.GUIDANCE_TRIGGERED_PREFIX)) {
        let guidance = message.triggered_guidance?.[0];
        return {
          type: 'guidance_triggered',
          title: guidance?.title,
          ruleText: guidance?.rule_text,
          guidanceUrl: this.guidanceUrl(
            message.content.slice(this.GUIDANCE_TRIGGERED_PREFIX.length),
            guidance?.guideline_id,
          ),
        };
      }

      return { type: 'system', content: message.content };
    });
  }

  get transcriptStatusText() {
    if (
      this.args.recording.transcriptStatus === 'not_submitted' ||
      this.args.recording.transcriptStatus === 'submitted'
    ) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.processing');
    }

    if (this.args.recording.transcriptStatus === 'failed') {
      return this.intl.t('inbox.phone-details-sidebar.transcript.failed');
    }

    if (this.recordingDeleted) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.deleted');
    }

    return this.intl.t('inbox.phone-details-sidebar.transcript.none');
  }

  get dropdownOptions(): Array<{ items: Option[] }> {
    let optionList: Option[];

    let downloadRecording: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.download.recording'),
      icon: 'download',
      onSelectItem: () => (this.recordingUrl ? this.downloadFile(this.recordingUrl) : undefined),
      isDestructive: false,
      isDisabled: !this.recordingUrl || this.recordingDeleted,
    };

    let downloadTranscript: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.download.transcript'),
      icon: 'download',
      onSelectItem: () => (this.transcriptUrl ? this.downloadFile(this.transcriptUrl) : undefined),
      isDestructive: false,
      isDisabled: !this.transcriptUrl || this.recordingDeleted,
    };

    let canDeleteRecordings = this.session.teammate.permissions.canDeleteCallRecordings;

    let deleteAll: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.delete-all'),
      icon: 'trash',
      onSelectItem: () => {
        this.enableConfirmationModal();
      },
      isDestructive: true,
      isDisabled: !this.recordingUrl || this.recordingDeleted || !canDeleteRecordings,
      tooltipText: !canDeleteRecordings
        ? this.intl.t('inbox.phone-details-sidebar.delete-permission-tooltip')
        : undefined,
    };

    optionList = [downloadRecording, downloadTranscript, deleteAll];

    return [{ items: optionList }];
  }

  private guidanceUrl(matchType: string, guidelineId?: string): string | null {
    if (!guidelineId) {
      return null;
    }
    let appId = this.session.workspace.id;
    if (matchType === 'escalation') {
      return `/a/apps/${appId}/automation/fin-ai-agent/escalation`;
    }
    return `/a/apps/${appId}/automation/fin-ai-agent/guidance/${guidelineId}`;
  }

  private downloadFile(url: string): void {
    safeWindowOpen(url, '_blank');
  }

  private isSystemMessage(message: Message): boolean {
    return message.speaker_label === 'System' || message.speaker === 'System';
  }

  private speakerLabel(message: Message): string {
    let speaker = message.speaker || message.speaker_label || '';

    switch (speaker.substring(0, this.PREFIX_LENGTH)) {
      case this.VOICEMAIL_PREFIX:
        return this.intl.t('inbox.phone-details-sidebar.speakers.voicemail');
      case this.SPEAKER_PREFIX: {
        let speaker_number =
          (parseInt(speaker.substring(this.PREFIX_LENGTH, speaker.length), 10) || 0) + 1;
        return `${this.intl.t('inbox.phone-details-sidebar.speakers.speaker', {
          speakerIdentifier: speaker_number,
        })}`;
      }
      default:
        return speaker;
    }
  }

  @action
  enableConfirmationModal() {
    this.showConfirmationModal = true;
  }

  @action
  async performDelete() {
    this.isDeleting = true;
    this.showConfirmationModal = false;

    try {
      let recordingId = this.args.recording.id;
      let callId = this.args.callId?.toString() || '';

      // Use recording ID if it's a numeric database ID
      // Synthetic IDs like "main-233" or "fin-233" indicate no database record exists
      // Extract numeric part from synthetic ID for call-level delete
      let isNumericId = /^\d+$/.test(recordingId);
      let url: string;

      if (isNumericId) {
        // Real database recording ID - delete specific recording
        url = `/call/${callId}/recordings/${recordingId}`;
      } else {
        // Synthetic ID (e.g., "main-233", "fin-233") - extract numeric part and delete at call level
        let numericPart = recordingId.match(/\d+$/)?.[0] || callId;
        url = `/call/${numericPart}/recordings`;
      }

      await deleteRequest(url);

      this.args.recording.isDeleted = true;
      this.args.recording.recordingStatus = 'deleted';

      this.snackbar.notify(this.intl.t('inbox.phone-details-sidebar.transcript.deleted'), {
        type: 'confirmation',
      });

      // Close the phone details sidebar so user can reload to see updated state
      this.inboxSidebarService.isViewingPhoneDetails = false;
    } catch (error) {
      console.error('Failed to delete recording:', error);
      this.snackbar.notifyError(
        this.intl.t('inbox.phone-details-sidebar.transcript.deletion-failed'),
      );
    } finally {
      this.isDeleting = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneDetailsCallTranscription': typeof PhoneDetailsCallTranscription;
    'inbox2/phone-details-call-transcription': typeof PhoneDetailsCallTranscription;
  }
}
